import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { defer, from, iif, Observable, of, throwError } from "rxjs";
import { tap, switchMap, map, catchError } from "rxjs/operators";
import { AuthService } from "../services/auth.service";
import { EmptyProductHttpHeaderError } from "src/app/errors/empty-product-http-header.error";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { UnknownProductHttpHeaderError } from "src/app/errors/unknow-product-http-header.error";
import { NoTenantOfProductHttpHeaderError } from "src/app/errors/no-tenant-of-product-http-header.error";
import { Mutex, MutexInterface } from "async-mutex";
import { HTTP_CONTEXT_TOKEN_NO_TOKEN } from "src/app/core/http-context-tokens";

const mutexRefreshMemberToken = new Mutex();

class ByProductAuthorizationHeaderInterceptor implements AuthorizationHeaderInterceptor {
  constructor(
    private authService: AuthService,
    private bootstrapService: BootstrapService
  ) {}

  intercept(
    req: HttpRequest<any>,
    flags: { refreshTokenRequest: boolean }
  ): Observable<HttpRequest<any>> {
    if (req.context?.get(HTTP_CONTEXT_TOKEN_NO_TOKEN)) {
      return of(req);
    }

    const product: "rocket" | string | null = req.headers.get("snt-product");
    req.headers.delete("snt-product");

    if (!product) {
      return throwError(() => new EmptyProductHttpHeaderError());
    }

    if (product !== "rocket") {
      // TODO riscrivere con mappa per prodotto
      return throwError(() => new UnknownProductHttpHeaderError());
    }

    return defer(() =>
      from(
        this.setMemberTokenByProduct(req, product, flags.refreshTokenRequest)
      )
    );
  }

  private async setMemberTokenByProduct(
    req: HttpRequest<any>,
    product: string,
    noRefresh: boolean
  ): Promise<HttpRequest<any>> {
    const currentTenant = this.bootstrapService.getCurrentTenant();
    const currentMemberAuth = this.bootstrapService.getCurrentMemberAuth();

    // se la richiesta richiede un token dello stesso prodotto di quello corrente
    if (currentMemberAuth && currentTenant?.type === product) {
      try {
        const token = await this.authService.getValidBearerToken(
          {
            id: currentMemberAuth?.id,
            account: currentMemberAuth?.account,
            token: currentMemberAuth?.token
          },
          currentTenant.type,
          noRefresh
        );

        if (!token) {
          throw new Error("Empty member token by product");
        }

        return req.clone({
          headers: req.headers.set("Member", token)
        });
      } catch (error) {
        throw new Error(`Error: ${error}`);
      }
    }

    // altrimenti cerco un member con un tenant del prodotto di quello richiesto
    // dalla richiesta
    const allMembers = this.bootstrapService.getAllMembers();

    const memberByProduct = allMembers?.find((x) => x.tenant.type === product);

    if (!memberByProduct) {
      throw new NoTenantOfProductHttpHeaderError(product);
    }

    try {
      // ho trovato il member che soddisfa il prodotto richiesto nella richiesta
      // richiedo un token valido, se necessario verrà refreshato
      const token = await this.authService.getValidBearerToken(
        memberByProduct.member,
        memberByProduct.tenant.type,
        noRefresh
      );

      if (!token) {
        throw new Error("Empty member token by product");
      }

      return req.clone({
        headers: req.headers.set("Member", token)
      });
    } catch (error) {
      throw new Error(`Error: ${error}`);
    }
  }
}

class DefaultAuthorizationHeaderInterceptor implements AuthorizationHeaderInterceptor {
  constructor(private authService: AuthService) {}

  intercept(
    req: HttpRequest<any>,
    flags: { refreshTokenRequest: boolean }
  ): Observable<HttpRequest<any>> {
    if (req.context?.get(HTTP_CONTEXT_TOKEN_NO_TOKEN)) {
      return of(req);
    }

    return defer(() =>
      from(this.setMemberToken(req, flags.refreshTokenRequest))
    );
  }

  private async setMemberToken(
    req: HttpRequest<any>,
    noRefresh: boolean
  ): Promise<HttpRequest<any>> {
    const memberToken = await this.authService.getMemberBearerToken(noRefresh);

    if (memberToken) {
      return req.clone({
        headers: req.headers.set("Member", memberToken)
      });
    }

    return req;
  }
}

interface AuthorizationHeaderInterceptor {
  intercept(
    req: HttpRequest<any>,
    flags: { refreshTokenRequest: boolean }
  ): Observable<HttpRequest<any>>;
}

@Injectable()
export class MemberTokenHeaderInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly bootstrapService = inject(BootstrapService);

  private readonly defaultInterceptor: AuthorizationHeaderInterceptor =
    new DefaultAuthorizationHeaderInterceptor(this.authService);
  private readonly byProductInterceptor: AuthorizationHeaderInterceptor =
    new ByProductAuthorizationHeaderInterceptor(
      this.authService,
      this.bootstrapService
    );

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (
      req.url.startsWith("noauth::") ||
      req.url.startsWith("https://snoots-storage") ||
      req.url.indexOf("1ftcxqvb71") >= 0 ||
      req.url.indexOf("snoots-storage-staging-filebucket") >= 0 ||
      req.url.indexOf("snoots-prod-storage-filebucket") >= 0
    ) {
      return next.handle(
        req.clone({
          url: req.url.replace("noauth::", "")
        })
      );
    }

    if (req.context?.get(HTTP_CONTEXT_TOKEN_NO_TOKEN)) {
      return next.handle(req);
    }

    return iif(
      () => isMemberTokenRefreshRequest(req),
      defer(() => {
        return of({
          refreshMemberTokenRequest: true,
          mutexReleaser: undefined
        });
      }),
      defer(() =>
        from(mutexRefreshMemberToken.acquire()).pipe(
          map((mutexReleaser: MutexInterface.Releaser) => ({
            refreshMemberTokenRequest: false,
            mutexReleaser
          }))
        )
      )
    ).pipe(
      switchMap(
        ({
          refreshMemberTokenRequest,
          mutexReleaser
        }: {
          refreshMemberTokenRequest: boolean;
          mutexReleaser: MutexInterface.Releaser | undefined;
        }) => {
          let observableRequest: Observable<HttpRequest<any>>;

          switch (true) {
            case req.headers.has("snt-product"): {
              observableRequest = this.byProductInterceptor.intercept(req, {
                refreshTokenRequest: refreshMemberTokenRequest
              });
              break;
            }
            default: {
              observableRequest = this.defaultInterceptor.intercept(req, {
                refreshTokenRequest: refreshMemberTokenRequest
              });
              break;
            }
          }

          return observableRequest.pipe(
            tap(() => {
              mutexReleaser && mutexReleaser();
            }),
            catchError((error) => {
              console.error(error);

              mutexReleaser && mutexReleaser();

              return throwError(error);
            }),
            switchMap((req) => next.handle(req))
          );
        }
      )
    );
  }
}

function isMemberTokenRefreshRequest(req: HttpRequest<any>): boolean {
  let hasToken = false;

  if (req.headers.has("x-request-refresh-member-token")) {
    hasToken = true;
    req.headers.delete("x-request-refresh-member-token");
  }

  return hasToken;
}
